/**
 * The quiz rules (piece 30, Training and quizzes, Quiz authoring, Quiz
 * analytics): what a question needs before it saves, the snapshot a Publish
 * freezes, the shuffle at take time, the grading of a sitting, the retake
 * cooldown, and the per-question statistics over first attempts.
 */
import { QUIZ_QUESTION_TYPES, type QuizQuestionTypeValue } from './model';

export interface QuizAnswerDraft {
  id?: string | null;
  text: string;
  isCorrect: boolean;
  sortOrder: number;
}

export interface QuizQuestionDraft {
  id?: string | null;
  prompt: string;
  type: QuizQuestionTypeValue;
  sortOrder: number;
  answers: readonly QuizAnswerDraft[];
}

/** The two answers a true/false question carries, in this order. */
export const TRUE_FALSE_ANSWERS = ['True', 'False'] as const;

/**
 * Why a question cannot be saved, or null. 🔨 A question with no correct
 * answer marked is refused, because it makes the quiz unpassable for everyone
 * who takes it before somebody notices.
 */
export function questionProblem(question: QuizQuestionDraft): string | null {
  if (!(QUIZ_QUESTION_TYPES as readonly string[]).includes(question.type)) return `${String(question.type)} is not a question type; it is single-choice, multiple-choice or true/false`;
  if (!question.prompt || question.prompt.trim() === '') return 'a question has a prompt';
  if (question.answers.some((answer) => !answer.text || answer.text.trim() === '')) return 'every answer has its text';
  const correct = question.answers.filter((answer) => answer.isCorrect).length;
  if (question.type === 'TrueFalse') {
    const texts = question.answers.map((answer) => answer.text.trim());
    if (texts.length !== 2 || texts[0] !== TRUE_FALSE_ANSWERS[0] || texts[1] !== TRUE_FALSE_ANSWERS[1]) return 'a true/false question carries the two answers True and False';
  } else if (question.answers.length < 2) {
    return 'a choice question offers at least two answers';
  }
  if (correct === 0) return `the question “${question.prompt.trim()}” has no correct answer marked, so nobody could pass it`;
  if (question.type !== 'MultipleChoice' && correct > 1) return 'a single-choice or true/false question has exactly one correct answer';
  return null;
}

/** Why a quiz cannot be saved, or null: the passing score, the cooldown, and every question. */
export function quizProblem(quiz: { passingScore: number; retakeCooldownMinutes: number | null; questions: readonly QuizQuestionDraft[] }): string | null {
  if (!Number.isInteger(quiz.passingScore) || quiz.passingScore < 1 || quiz.passingScore > 100) return 'the passing score is a whole percentage from 1 to 100';
  if (quiz.retakeCooldownMinutes !== null && (!Number.isInteger(quiz.retakeCooldownMinutes) || quiz.retakeCooldownMinutes < 0)) return 'the retake cooldown is a whole number of minutes, or none';
  for (const question of quiz.questions) {
    const problem = questionProblem(question);
    if (problem) return problem;
  }
  return null;
}

/** One question as a QuizVersion freezes it (decision 0109): the words, the type and every answer with its correctness. */
export interface SnapshotAnswer {
  id: string;
  text: string;
  isCorrect: boolean;
  sortOrder: number;
}

export interface SnapshotQuestion {
  id: string;
  prompt: string;
  type: QuizQuestionTypeValue;
  sortOrder: number;
  answers: SnapshotAnswer[];
}

/** The snapshot a Publish writes, in authoring order. */
export function snapshotQuestions(questions: readonly { id: string; prompt: string; type: QuizQuestionTypeValue; sortOrder: number; answers: readonly SnapshotAnswer[] }[]): SnapshotQuestion[] {
  return [...questions]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((question) => ({
      id: question.id,
      prompt: question.prompt,
      type: question.type,
      sortOrder: question.sortOrder,
      answers: [...question.answers].sort((a, b) => a.sortOrder - b.sortOrder).map((answer) => ({ id: answer.id, text: answer.text, isCorrect: answer.isCorrect, sortOrder: answer.sortOrder })),
    }));
}

/** A stored snapshot read back, with anything malformed dropped rather than trusted. */
export function readSnapshot(stored: unknown): SnapshotQuestion[] {
  if (!Array.isArray(stored)) return [];
  return stored.flatMap((raw) => {
    const q = raw as Partial<SnapshotQuestion>;
    if (typeof q?.id !== 'string' || typeof q.prompt !== 'string' || !Array.isArray(q.answers)) return [];
    const type = (QUIZ_QUESTION_TYPES as readonly string[]).includes(String(q.type)) ? (q.type as QuizQuestionTypeValue) : 'SingleChoice';
    const answers = q.answers.filter((a): a is SnapshotAnswer => typeof a?.id === 'string' && typeof a.text === 'string').map((a) => ({ id: a.id, text: a.text, isCorrect: a.isCorrect === true, sortOrder: Number(a.sortOrder) || 0 }));
    return [{ id: q.id, prompt: q.prompt, type, sortOrder: Number(q.sortOrder) || 0, answers }];
  });
}

/** Fisher–Yates over a copy, with the random source injectable so a suite can pin it. */
export function shuffled<T>(items: readonly T[], random: () => number = Math.random): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** One question as a taker sees it: no correctness, and the answers in a new order each sitting. */
export interface TakeQuestion {
  id: string;
  prompt: string;
  type: QuizQuestionTypeValue;
  answers: { id: string; text: string }[];
}

/**
 * 🔨 Answer order shuffles at take time; `sortOrder` is the authoring order only,
 * or the answer to question 3 is the second one and people pass by memorising
 * positions. A true/false question keeps True before False, which is not a
 * position anybody can memorise an answer by.
 */
export function takeView(snapshot: readonly SnapshotQuestion[], random: () => number = Math.random): TakeQuestion[] {
  return snapshot.map((question) => ({
    id: question.id,
    prompt: question.prompt,
    type: question.type,
    answers: (question.type === 'TrueFalse' ? question.answers : shuffled(question.answers, random)).map((answer) => ({ id: answer.id, text: answer.text })),
  }));
}

/** What a taker sends: the options chosen for each question. */
export interface GivenAnswer {
  questionId: string;
  answerIds: readonly string[];
}

export interface GradedQuestion {
  questionId: string;
  correct: boolean;
  chosen: string[];
}

export interface GradedSitting {
  /** The percentage of questions answered correctly, to one decimal. */
  score: number;
  passed: boolean;
  questions: GradedQuestion[];
  /** 🔨 The questions answered wrongly, and never the correct answer (product owner, 2026-08-31). */
  wrongQuestionIds: string[];
}

/**
 * A sitting graded against the version sat. A question is right when the
 * options chosen are exactly its correct ones, so a multiple-choice question
 * half answered is wrong. An option the version does not carry is ignored.
 */
export function gradeSitting(snapshot: readonly SnapshotQuestion[], given: readonly GivenAnswer[], passingScore: number): GradedSitting {
  const questions = snapshot.map((question) => {
    const offered = new Set(question.answers.map((answer) => answer.id));
    const chosen = [...new Set((given.find((g) => g.questionId === question.id)?.answerIds ?? []).filter((id) => offered.has(id)))];
    const correctIds = question.answers.filter((answer) => answer.isCorrect).map((answer) => answer.id);
    const correct = chosen.length === correctIds.length && correctIds.every((id) => chosen.includes(id));
    return { questionId: question.id, correct, chosen };
  });
  const right = questions.filter((q) => q.correct).length;
  const score = questions.length === 0 ? 0 : Math.round((right / questions.length) * 1000) / 10;
  return { score, passed: questions.length > 0 && score >= passingScore, questions, wrongQuestionIds: questions.filter((q) => !q.correct).map((q) => q.questionId) };
}

/** The questions a sitting left unanswered, which the page names before it submits. */
export function unansweredQuestions(snapshot: readonly { id: string }[], given: readonly GivenAnswer[]): string[] {
  return snapshot.filter((question) => !(given.find((g) => g.questionId === question.id)?.answerIds.length)).map((question) => question.id);
}

/**
 * 🔨 A retake inside `Quiz.retakeCooldownMinutes` after a failed sitting is
 * refused, one outside it is allowed, and a null cooldown refuses nothing. No
 * field switches retakes off. Answers the minutes left, or null.
 */
export function retakeWaitMinutes(lastFailedAt: Date | null, cooldownMinutes: number | null, now: Date): number | null {
  if (cooldownMinutes === null || cooldownMinutes <= 0 || lastFailedAt === null) return null;
  const opensAt = lastFailedAt.getTime() + cooldownMinutes * 60_000;
  if (now.getTime() >= opensAt) return null;
  return Math.ceil((opensAt - now.getTime()) / 60_000);
}

/** One sitting and one answer row as the statistics read them. */
export interface StatAttempt {
  id: string;
  personId: string;
  submittedAt: Date;
}

export interface StatAnswer {
  attemptId: string;
  questionId: string;
  answerId: string | null;
  isCorrect: boolean;
}

export interface QuestionStat {
  questionId: string;
  prompt: string;
  /** People who have sat this version at least once. */
  firstAttempts: number;
  firstAttemptFailures: number;
  /** Whole percent, or null before anybody has sat it. */
  failureRatePercent: number | null;
  /** 🔨 The wrong options chosen on first attempts and how often, beside the rate (piece 30, Quiz analytics). */
  wrongAnswers: { answerId: string | null; text: string; count: number }[];
}

/** Each person's first sitting of one version, and only that. */
export function firstAttempts(attempts: readonly StatAttempt[]): StatAttempt[] {
  const first = new Map<string, StatAttempt>();
  for (const attempt of attempts) {
    const held = first.get(attempt.personId);
    if (!held || attempt.submittedAt.getTime() < held.submittedAt.getTime()) first.set(attempt.personId, attempt);
  }
  return [...first.values()];
}

/**
 * 🔨 The question report over one QuizVersion's first attempts alone: a retake
 * moves no published figure, and attempts against two versions are never
 * pooled, because the caller passes one version's rows (piece 30, Quiz analytics).
 */
export function questionStats(snapshot: readonly SnapshotQuestion[], attempts: readonly StatAttempt[], answers: readonly StatAnswer[]): QuestionStat[] {
  const first = new Set(firstAttempts(attempts).map((attempt) => attempt.id));
  const counted = answers.filter((answer) => first.has(answer.attemptId));
  return snapshot.map((question) => {
    const rows = counted.filter((answer) => answer.questionId === question.id);
    const byAttempt = new Map<string, StatAnswer[]>();
    for (const row of rows) byAttempt.set(row.attemptId, [...(byAttempt.get(row.attemptId) ?? []), row]);
    const sittings = first.size;
    let failures = 0;
    const wrong = new Map<string, number>();
    for (const attemptId of first) {
      const given = byAttempt.get(attemptId) ?? [];
      const right = given.length > 0 && given.every((row) => row.isCorrect);
      if (right) continue;
      failures += 1;
      const correctIds = new Set(question.answers.filter((a) => a.isCorrect).map((a) => a.id));
      const picked = given.filter((row) => row.answerId === null || !correctIds.has(row.answerId));
      if (picked.length === 0) wrong.set('', (wrong.get('') ?? 0) + 1);
      for (const row of picked) wrong.set(row.answerId ?? '', (wrong.get(row.answerId ?? '') ?? 0) + 1);
    }
    const wrongAnswers = [...wrong.entries()]
      .map(([answerId, count]) => ({ answerId: answerId || null, text: question.answers.find((a) => a.id === answerId)?.text ?? '', count }))
      .sort((a, b) => b.count - a.count);
    return {
      questionId: question.id,
      prompt: question.prompt,
      firstAttempts: sittings,
      firstAttemptFailures: failures,
      failureRatePercent: sittings === 0 ? null : Math.round((failures / sittings) * 100),
      wrongAnswers,
    };
  });
}

/**
 * 🔨 A question crosses the flag when its first-attempt failure rate reaches the
 * threshold over at least the minimum sample; below the sample it never does,
 * or the flag cries defect on every new quiz (piece 30, Quiz analytics).
 */
export function crossesFailureThreshold(stat: Pick<QuestionStat, 'firstAttempts' | 'failureRatePercent'>, thresholdPercent: number, minimumAttempts: number): boolean {
  return stat.firstAttempts >= minimumAttempts && stat.failureRatePercent !== null && stat.failureRatePercent >= thresholdPercent;
}
